import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import * as api from 'api/api'
import { EquipmentAsset, EquipmentAssetInput, EquipmentOwnership, EquipmentStatus, Site } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { useConfirm } from 'components/Confirm'
import { Icon } from 'components/Icon'
import {
  Button,
  EmptyState,
  ErrorState,
  IconButton,
  SelectField,
  Sheet,
  Spinner,
  TextField,
} from 'components/ui'
import { EQUIPMENT_OWNERSHIP_LABELS, EQUIPMENT_STATUS_BADGE_CLASS, EQUIPMENT_STATUS_LABELS, formatNumber } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

const STATUS_OPTIONS: EquipmentStatus[] = ['WORKING', 'IDLE', 'BREAKDOWN']
const OWNERSHIP_OPTIONS: EquipmentOwnership[] = ['OWNED', 'RENTED']

const EMPTY_FORM: EquipmentAssetInput = {
  currentSiteId: null,
  dailyRate: null,
  hoursThisMonth: null,
  model: '',
  name: '',
  ownership: 'OWNED',
  rentalVendor: '',
  status: 'IDLE',
  type: '',
}

const EquipmentForm = ({
  initial,
  sites,
  busy,
  onCancel,
  onSubmit,
}: {
  initial?: EquipmentAsset
  sites: Site[]
  busy: boolean
  onCancel: () => void
  onSubmit: (input: EquipmentAssetInput) => void
}) => {
  const [form, setForm] = useState<EquipmentAssetInput>(() =>
    initial
      ? {
          currentSiteId: initial.currentSiteId ?? null,
          dailyRate: initial.dailyRate ?? null,
          hoursThisMonth: initial.hoursThisMonth ?? null,
          model: initial.model ?? '',
          name: initial.name,
          ownership: initial.ownership,
          rentalVendor: initial.rentalVendor ?? '',
          status: initial.status,
          type: initial.type,
        }
      : EMPTY_FORM
  )

  const valid = form.name.trim() && form.type.trim() && (form.ownership === 'OWNED' || form.rentalVendor?.trim())

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!valid) return
    onSubmit({
      ...form,
      currentSiteId: form.currentSiteId || null,
      dailyRate: form.ownership === 'RENTED' ? (form.dailyRate ? Number(form.dailyRate) : null) : null,
      hoursThisMonth: form.hoursThisMonth ? Number(form.hoursThisMonth) : null,
      model: form.model?.trim() || null,
      name: form.name.trim(),
      rentalVendor: form.ownership === 'RENTED' ? form.rentalVendor?.trim() || null : null,
      type: form.type.trim(),
    })
  }

  return (
    <Sheet
      title={initial ? t('تعديل معدة', 'Edit equipment') : t('إضافة معدة', 'Add equipment')}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t('إلغاء', 'Cancel')}</Button>
          <Button type="submit" form="equipment-form" variant="primary" busy={busy} disabled={!valid}>
            {initial ? t('حفظ', 'Save') : t('إضافة', 'Add')}
          </Button>
        </>
      }
    >
      <form id="equipment-form" className="form-grid" onSubmit={submit}>
        <TextField
          label={t('اسم المعدة', 'Equipment name')}
          className="span-2"
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          placeholder={t('مثال: رافعة برجية TC-02', 'Example: Tower crane TC-02')}
          autoFocus
          required
        />
        <TextField
          label={t('النوع', 'Type')}
          value={form.type}
          onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))}
          placeholder={t('مثال: رافعة برجية', 'Example: Tower crane')}
          required
        />
        <TextField
          label={t('الموديل', 'Model')}
          value={form.model ?? ''}
          onChange={(event) => setForm((current) => ({ ...current, model: event.target.value }))}
          placeholder={t('مثال: Liebherr 280 EC-H', 'Example: Liebherr 280 EC-H')}
        />

        <div className="span-2 radio-card-row" role="radiogroup" aria-label={t('الملكية', 'Ownership')}>
          <button
            type="button"
            className={form.ownership === 'OWNED' ? 'radio-card is-checked' : 'radio-card'}
            onClick={() => setForm((current) => ({ ...current, ownership: 'OWNED' }))}
          >
            <span className="radio-card__radio" />
            <span className="radio-card__icon">
              <Icon name="truck" size={16} />
            </span>
            <span className="radio-card__text">
              <strong>{EQUIPMENT_OWNERSHIP_LABELS.OWNED}</strong>
              <span className="muted small">{t('ضمن أسطول الشركة', 'Part of the company fleet')}</span>
            </span>
          </button>
          <button
            type="button"
            className={form.ownership === 'RENTED' ? 'radio-card is-checked' : 'radio-card'}
            onClick={() => setForm((current) => ({ ...current, ownership: 'RENTED' }))}
          >
            <span className="radio-card__radio" />
            <span className="radio-card__icon">
              <Icon name="calendar" size={16} />
            </span>
            <span className="radio-card__text">
              <strong>{EQUIPMENT_OWNERSHIP_LABELS.RENTED}</strong>
              <span className="muted small">{t('بالتعاقد اليومي', 'On a daily hire')}</span>
            </span>
          </button>
        </div>

        {form.ownership === 'RENTED' ? (
          <>
            <TextField
              label={t('الجهة المؤجرة', 'Rental company')}
              value={form.rentalVendor ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, rentalVendor: event.target.value }))}
              required
            />
            <TextField
              label={t('الأجرة اليومية (د.أ)', 'Daily rate (JOD)')}
              type="number"
              min={0}
              step="0.01"
              value={form.dailyRate ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, dailyRate: event.target.value ? Number(event.target.value) : null }))}
            />
          </>
        ) : null}

        <SelectField
          label={t('منطقة العمل الحالية', 'Current work area')}
          value={form.currentSiteId ?? ''}
          onChange={(event) => setForm((current) => ({ ...current, currentSiteId: event.target.value || null }))}
        >
          <option value="">{t('في الساحة (غير مُسندة)', 'In the yard (unassigned)')}</option>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>
              {site.name} · {site.projectName}
            </option>
          ))}
        </SelectField>

        <SelectField
          label={t('الحالة', 'Status')}
          value={form.status}
          onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as EquipmentStatus }))}
          required
        >
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {EQUIPMENT_STATUS_LABELS[status]}
            </option>
          ))}
        </SelectField>

        <TextField
          label={t('ساعات التشغيل هذا الشهر', 'Operating hours this month')}
          type="number"
          min={0}
          className="span-2"
          hint={t('قيمة يحدّثها المسؤول يدوياً — لا يوجد ربط تلقائي بساعات التشغيل المسجّلة في التقارير بعد.', 'An admin updates this by hand — operating hours on reports are not linked here yet.')}
          value={form.hoursThisMonth ?? ''}
          onChange={(event) => setForm((current) => ({ ...current, hoursThisMonth: event.target.value ? Number(event.target.value) : null }))}
        />
      </form>
    </Sheet>
  )
}

/** Global equipment fleet registry — admin console, portfolio-wide (Figma: Admin / 04 · Equipment, 05 · Add equipment).
 *  Distinct from the per-project equipment-name catalogue (still used when filling in a report's equipment section):
 *  this tracks individually-identified units (serial code, ownership, operating status, site assignment). */
export const EquipmentFleetPage = () => {
  const { user } = useAuth()
  const [params] = useSearchParams()
  const { data: equipment, error, loading, reload, setData } = useAsync(api.fetchEquipmentAssets, [])
  const { data: sites } = useAsync(api.fetchAllSites, [])
  const { busy, run } = useAction()
  const confirm = useConfirm()

  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set())
  const [ownershipFilter, setOwnershipFilter] = useState<Set<EquipmentOwnership>>(new Set())
  const [siteFilter, setSiteFilter] = useState<Set<string>>(() => (params.get('site') ? new Set([params.get('site')!]) : new Set()))
  const [projectFilter] = useState<string | null>(params.get('project'))
  const [statusFilter, setStatusFilter] = useState<Set<EquipmentStatus>>(new Set())
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<EquipmentAsset | null>(null)

  const typeOptions = useMemo(() => Array.from(new Set((equipment ?? []).map((item) => item.type))).sort(), [equipment])

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (equipment ?? []).filter((item) => {
      if (projectFilter && item.currentProjectId !== projectFilter) return false
      if (typeFilter.size && !typeFilter.has(item.type)) return false
      if (ownershipFilter.size && !ownershipFilter.has(item.ownership)) return false
      if (siteFilter.size && !siteFilter.has(item.currentSiteId ?? '')) return false
      if (statusFilter.size && !statusFilter.has(item.status)) return false
      if (
        term &&
        !item.code.toLowerCase().includes(term) &&
        !item.name.toLowerCase().includes(term) &&
        !(item.model ?? '').toLowerCase().includes(term)
      )
        return false
      return true
    })
  }, [equipment, search, typeFilter, ownershipFilter, siteFilter, statusFilter, projectFilter])

  useEffect(() => {
    setPage(1)
  }, [search, typeFilter, ownershipFilter, siteFilter, statusFilter, pageSize])

  if (!user?.isAdmin) return <ForbiddenPage message={t('سجل المعدات متاح لمدير النظام فقط.', 'The equipment registry is available to admins only.')} />

  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize))
  const safePage = Math.min(page, pageCount)
  const pageItems = visible.slice((safePage - 1) * pageSize, safePage * pageSize)
  const pageStart = visible.length ? (safePage - 1) * pageSize + 1 : 0
  const pageEnd = Math.min(safePage * pageSize, visible.length)

  const ownedCount = equipment?.filter((item) => item.ownership === 'OWNED').length ?? 0
  const rentedCount = equipment?.filter((item) => item.ownership === 'RENTED').length ?? 0
  const idleCount = equipment?.filter((item) => item.status === 'IDLE').length ?? 0

  const toggle = <T,>(set: Set<T>, setter: (next: Set<T>) => void, value: T) => {
    const next = new Set(set)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    setter(next)
  }

  const create = async (input: EquipmentAssetInput) => {
    const created = await run('add', () => api.createEquipmentAsset(input), t('تمت إضافة المعدة', 'Equipment added'))
    if (created) {
      setData((current) => [...(current ?? []), created])
      setAdding(false)
    }
  }

  const update = async (input: EquipmentAssetInput) => {
    if (!editing) return
    const updated = await run(`edit${editing.id}`, () => api.updateEquipmentAsset(editing.id, input), t('تم حفظ التعديلات', 'Changes saved'))
    if (updated) {
      setData((current) => current?.map((item) => (item.id === updated.id ? updated : item)))
      setEditing(null)
    }
  }

  const remove = async (item: EquipmentAsset) => {
    if (!(await confirm(t(`حذف «${item.name}» (${item.code}) من سجل المعدات؟`, `Delete "${item.name}" (${item.code}) from the equipment registry?`), { confirmLabel: t('حذف', 'Delete') }))) return
    const done = await run(`rm${item.id}`, () => api.removeEquipmentAsset(item.id), t('تم الحذف', 'Deleted'))
    if (done) setData((current) => current?.filter((entry) => entry.id !== item.id))
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('المعدات', 'Equipment')}</h1>
          <p className="muted">
            {equipment
              ? t(
                  `${equipment.length} قطعة · ${ownedCount} مملوكة، ${rentedCount} مستأجرة · ${idleCount} خاملة الآن`,
                  `${equipment.length} units · ${ownedCount} Owned, ${rentedCount} Rented · ${idleCount} Idle now`
                )
              : ''}
          </p>
        </div>
        <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
          {t('إضافة معدة', 'Add equipment')}
        </Button>
      </div>

      <div className="toolbar">
        <div className="search">
          <Icon name="search" size={16} />
          <label htmlFor="equipment-search" className="sr-only">
            {t('بحث', 'Search')}
          </label>
          <input
            id="equipment-search"
            type="search"
            placeholder={t('بحث بالرقم أو الاسم أو الموديل...', 'Search by number, name, or model...')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {typeOptions.length ? (
          <details className="dropdown">
            <summary className="btn">{t('النوع', 'Type')}{typeFilter.size ? ` (${typeFilter.size})` : ''}</summary>
            <div className="dropdown__panel">
              {typeOptions.map((type) => (
                <label key={type} className="dropdown__item">
                  <input type="checkbox" checked={typeFilter.has(type)} onChange={() => toggle(typeFilter, setTypeFilter, type)} />
                  {type}
                </label>
              ))}
            </div>
          </details>
        ) : null}
        <details className="dropdown">
          <summary className="btn">{t('الملكية', 'Ownership')}{ownershipFilter.size ? ` (${ownershipFilter.size})` : ''}</summary>
          <div className="dropdown__panel">
            {OWNERSHIP_OPTIONS.map((ownership) => (
              <label key={ownership} className="dropdown__item">
                <input
                  type="checkbox"
                  checked={ownershipFilter.has(ownership)}
                  onChange={() => toggle(ownershipFilter, setOwnershipFilter, ownership)}
                />
                {EQUIPMENT_OWNERSHIP_LABELS[ownership]}
              </label>
            ))}
          </div>
        </details>
        {sites?.length ? (
          <details className="dropdown">
            <summary className="btn">{t('منطقة العمل', 'Work area')}{siteFilter.size ? ` (${siteFilter.size})` : ''}</summary>
            <div className="dropdown__panel">
              <label className="dropdown__item">
                <input type="checkbox" checked={siteFilter.has('')} onChange={() => toggle(siteFilter, setSiteFilter, '')} />
                {t('في الساحة', 'In the yard')}
              </label>
              {sites.map((site) => (
                <label key={site.id} className="dropdown__item">
                  <input type="checkbox" checked={siteFilter.has(site.id)} onChange={() => toggle(siteFilter, setSiteFilter, site.id)} />
                  {site.name} · {site.projectName}
                </label>
              ))}
            </div>
          </details>
        ) : null}
        <details className="dropdown">
          <summary className="btn">{t('الحالة', 'Status')}{statusFilter.size ? ` (${statusFilter.size})` : ''}</summary>
          <div className="dropdown__panel">
            {STATUS_OPTIONS.map((status) => (
              <label key={status} className="dropdown__item">
                <input type="checkbox" checked={statusFilter.has(status)} onChange={() => toggle(statusFilter, setStatusFilter, status)} />
                {EQUIPMENT_STATUS_LABELS[status]}
              </label>
            ))}
          </div>
        </details>
      </div>

      {loading && !equipment ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !equipment?.length ? (
        <EmptyState title={t('لا توجد معدات مسجّلة بعد', 'No equipment recorded yet')} icon="truck">
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
            {t('إضافة معدة', 'Add equipment')}
          </Button>
        </EmptyState>
      ) : !visible.length ? (
        <EmptyState title={t('لا توجد نتائج مطابقة', 'No matching results')} icon="search" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('الرقم', 'Number')}</th>
                <th scope="col">{t('المعدة', 'Equipment')}</th>
                <th scope="col">{t('النوع', 'Type')}</th>
                <th scope="col">{t('الملكية', 'Ownership')}</th>
                <th scope="col">{t('منطقة العمل الحالية', 'Current work area')}</th>
                <th scope="col">{t('الحالة', 'Status')}</th>
                <th scope="col">{t('الساعات هذا الشهر', 'Hours this month')}</th>
                <th scope="col">
                  <span className="sr-only">{t('إجراءات', 'Actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item) => (
                <tr key={item.id}>
                  <td className="code-link">{item.code}</td>
                  <td>
                    <strong>{item.name}</strong>
                    {item.model ? <div className="muted small">{item.model}</div> : null}
                  </td>
                  <td>{item.type}</td>
                  <td>
                    <span className={`badge ${item.ownership === 'OWNED' ? 'badge--new' : 'badge--created'}`}>
                      {EQUIPMENT_OWNERSHIP_LABELS[item.ownership]}
                      {item.ownership === 'RENTED' && item.rentalVendor ? ` · ${item.rentalVendor}` : ''}
                    </span>
                  </td>
                  <td>
                    {item.currentSiteName ? (
                      <>
                        {item.currentSiteName}
                        <div className="muted small">{item.currentProjectName}</div>
                      </>
                    ) : (
                      <span className="muted">{t('في الساحة', 'In the yard')}</span>
                    )}
                  </td>
                  <td>
                    <span className={EQUIPMENT_STATUS_BADGE_CLASS[item.status]}>{EQUIPMENT_STATUS_LABELS[item.status]}</span>
                  </td>
                  <td>{item.hoursThisMonth != null ? `${formatNumber(item.hoursThisMonth)} ${t('س', 'h')}` : <span className="muted">—</span>}</td>
                  <td>
                    <div className="row-actions">
                      <IconButton icon="edit" label={t(`تعديل ${item.name}`, `Edit ${item.name}`)} onClick={() => setEditing(item)} />
                      <IconButton
                        icon="trash"
                        label={t(`حذف ${item.name}`, `Delete ${item.name}`)}
                        onClick={() => remove(item)}
                        disabled={busy === `rm${item.id}`}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-footer">
            <span className="muted small">{t(`${visible.length} قطعة`, `${visible.length} units`)}</span>
            <span className="spacer" />
            <label className="table-footer__rows">
              {t('صفوف لكل صفحة', 'Rows per page')}
              <select className="input" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
            <span className="muted small">
              {pageStart}–{pageEnd} {t('من', 'of')} {visible.length}
            </span>
            <div className="table-footer__pagination">
              <IconButton icon="chevronsRight" label={t('الصفحة الأولى', 'First page')} disabled={safePage <= 1} onClick={() => setPage(1)} />
              <IconButton
                icon="chevronRight"
                label={t('الصفحة السابقة', 'Previous page')}
                disabled={safePage <= 1}
                onClick={() => setPage(Math.max(1, safePage - 1))}
              />
              <IconButton
                icon="chevronLeft"
                label={t('الصفحة التالية', 'Next page')}
                disabled={safePage >= pageCount}
                onClick={() => setPage(Math.min(pageCount, safePage + 1))}
              />
              <IconButton icon="chevronsLeft" label={t('الصفحة الأخيرة', 'Last page')} disabled={safePage >= pageCount} onClick={() => setPage(pageCount)} />
            </div>
          </div>
        </div>
      )}

      {adding ? <EquipmentForm sites={sites ?? []} busy={busy === 'add'} onCancel={() => setAdding(false)} onSubmit={create} /> : null}
      {editing ? (
        <EquipmentForm
          initial={editing}
          sites={sites ?? []}
          busy={busy === `edit${editing.id}`}
          onCancel={() => setEditing(null)}
          onSubmit={update}
        />
      ) : null}
    </div>
  )
}
